/**
 * Makes a PDF lighter before it is uploaded, without a visible change:
 * - the file's structure is rewritten compactly (object streams, unused objects dropped),
 *   which loses nothing;
 * - photos stored at more than print resolution (over ~300 dpi on a full page) are
 *   scaled down to it and saved again as high-quality JPEG, only when that saves at
 *   least 15 %.
 * Runs in the browser (a Worker cannot run PDF tools). Anything unexpected keeps the
 * original: a PDF is never made worse or broken to save space. pdf-lib loads only here.
 */

/** Longest side of a photo after optimizing: an A4 page at 300 dpi. */
const MAX_SIDE = 2480;
const JPEG_QUALITY = 0.9;
/** Photos smaller than this are left alone: the gain is not worth a re-encode. */
const MIN_IMAGE_BYTES = 100 * 1024;
/** A new version of a photo has to be at least this much smaller to replace it. */
const MIN_SAVING = 0.15;

export interface OptimizedPdf {
  file: File;
  before: number;
  after: number;
}

export async function optimizePdf(file: File): Promise<OptimizedPdf> {
  const original = { file, before: file.size, after: file.size };
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    // Rewriting a signed PDF would void its signature.
    if (hasSignature(bytes)) return original;
    const { PDFDocument, PDFName, PDFNumber, PDFRawStream } = await import("pdf-lib");
    const doc = await PDFDocument.load(bytes, { updateMetadata: false });

    for (const [ref, obj] of doc.context.enumerateIndirectObjects()) {
      if (!(obj instanceof PDFRawStream)) continue;
      const dict = obj.dict;
      const name = (key: string) => dict.get(PDFName.of(key))?.toString();
      // Only plain RGB JPEG photos: other color spaces, masks or decode arrays would
      // not come back the same from a canvas.
      if (name("Subtype") !== "/Image" || name("Filter") !== "/DCTDecode") continue;
      if (name("ColorSpace") !== "/DeviceRGB" || name("BitsPerComponent") !== "8") continue;
      if (dict.has(PDFName.of("Decode")) || dict.has(PDFName.of("ImageMask"))) continue;
      if (obj.contents.byteLength < MIN_IMAGE_BYTES) continue;

      const smaller = await reencode(obj.contents);
      if (!smaller || smaller.bytes.byteLength > obj.contents.byteLength * (1 - MIN_SAVING)) continue;
      const next = dict.clone(doc.context);
      next.set(PDFName.of("Width"), PDFNumber.of(smaller.width));
      next.set(PDFName.of("Height"), PDFNumber.of(smaller.height));
      next.set(PDFName.of("Length"), PDFNumber.of(smaller.bytes.byteLength));
      next.delete(PDFName.of("DecodeParms"));
      doc.context.assign(ref, PDFRawStream.of(next, smaller.bytes));
    }

    const out = await doc.save({ useObjectStreams: true });
    if (out.byteLength >= file.size) return original;
    return {
      file: new File([out as Uint8Array<ArrayBuffer>], file.name, {
        type: "application/pdf",
        lastModified: file.lastModified,
      }),
      before: file.size,
      after: out.byteLength,
    };
  } catch (err) {
    console.warn("pdf.optimize_skipped", err);
    return original;
  }
}

function hasSignature(bytes: Uint8Array): boolean {
  // "/ByteRange" only appears in the signature dictionary of a signed PDF.
  const needle = [47, 66, 121, 116, 101, 82, 97, 110, 103, 101];
  outer: for (let i = 0; i <= bytes.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) if (bytes[i + j] !== needle[j]) continue outer;
    return true;
  }
  return false;
}

/** The photo at no more than MAX_SIDE pixels, as high-quality JPEG. */
async function reencode(jpeg: Uint8Array): Promise<{ bytes: Uint8Array; width: number; height: number } | null> {
  // PDF viewers ignore a JPEG's own rotation and color profile: so does the copy.
  const bitmap = await createImageBitmap(new Blob([jpeg as Uint8Array<ArrayBuffer>], { type: "image/jpeg" }), {
    imageOrientation: "none",
    colorSpaceConversion: "none",
  });
  try {
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await canvas.convertToBlob({ type: "image/jpeg", quality: JPEG_QUALITY });
    return { bytes: new Uint8Array(await blob.arrayBuffer()), width, height };
  } finally {
    bitmap.close();
  }
}
